import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Save, Upload, User, Star, Trash2, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { WorkshopEditor } from './WorkshopEditor';
import {
  IMAGE_ACCEPT,
  skippedPhotosToast,
  storageUploadMessage,
  uploadErrorToast,
  validateImageFile,
  withImageType,
} from '@/lib/imageUpload';

interface InstructorPhoto {
  id: string;
  photo_url: string;
  is_featured: boolean;
  sort_order: number;
}

interface WorkshopLeader {
  id: string;
  user_id: string;
  business_name: string;
  bio: string | null;
  full_bio: string | null;
  profile_photo_url: string | null;
  website_url: string | null;
  slug: string | null;
  paid_through: string;
  is_active: boolean;
}

interface Props {
  leader: WorkshopLeader;
  onUpdate: () => void;
  startEditing?: boolean;
  onEditingChange?: (editing: boolean) => void;
}

export const WorkshopLeaderProfile: React.FC<Props> = ({ leader, onUpdate, startEditing, onEditingChange }) => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(startEditing || false);

  React.useEffect(() => {
    if (startEditing) setEditing(true);
  }, [startEditing]);

  const handleSetEditing = (val: boolean) => {
    setEditing(val);
    onEditingChange?.(val);
  };
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    business_name: leader.business_name,
    bio: leader.bio || '',
    full_bio: leader.full_bio || '',
    website_url: leader.website_url || '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      // Auto-generate slug from business name
      const slug = form.business_name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

      const { error } = await supabase
        .from('workshop_leaders')
        .update({
          business_name: form.business_name,
          bio: form.bio || null,
          full_bio: form.full_bio || null,
          website_url: form.website_url || null,
          slug,
        })
        .eq('id', leader.id);

      if (error) throw error;
      toast.success('Profile updated');
      handleSetEditing(false);
      onUpdate();
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Gallery photos
  const [galleryPhotos, setGalleryPhotos] = useState<InstructorPhoto[]>([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  useEffect(() => {
    loadGalleryPhotos();
  }, [leader.id]);

  const loadGalleryPhotos = async () => {
    const { data } = await supabase
      .from('instructor_photos')
      .select('*')
      .eq('leader_id', leader.id)
      .order('sort_order');
    setGalleryPhotos(data || []);
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const picked = Array.from(input.files ?? []);
    if (picked.length === 0 || !user) {
      input.value = '';
      return;
    }

    // Refuse bad files up front and upload the rest, so one HEIC doesn't
    // cancel the whole pick (ERI-2836).
    const skipped: string[] = [];
    const filesToUpload = picked.filter((file) => {
      const problem = validateImageFile(file);
      if (problem) skipped.push(problem);
      return !problem;
    });

    let uploaded = 0;
    if (filesToUpload.length > 0) {
      setUploadingGallery(true);
      try {
        // Check if any photos exist already (for featured logic)
        const { count } = await supabase
          .from('instructor_photos')
          .select('id', { count: 'exact', head: true })
          .eq('leader_id', leader.id);
        let currentCount = count || 0;

        for (const file of filesToUpload) {
          try {
            const ext = file.name.split('.').pop();
            const path = `${user.id}/gallery/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

            const { error: uploadError } = await supabase.storage
              .from('workshop-images')
              .upload(path, withImageType(file));

            if (uploadError) throw uploadError;

            const { data: urlData } = supabase.storage
              .from('workshop-images')
              .getPublicUrl(path);

            await supabase.from('instructor_photos').insert({
              leader_id: leader.id,
              photo_url: urlData.publicUrl,
              is_featured: currentCount === 0, // Only first-ever photo is featured
              sort_order: currentCount,
            });
            currentCount++;
            uploaded++;
          } catch (err) {
            skipped.push(storageUploadMessage(err));
          }
        }
      } catch (err) {
        toast.error('Failed to upload photo');
      } finally {
        setUploadingGallery(false);
      }
    }

    input.value = '';
    if (uploaded > 0) {
      toast.success('Photos uploaded');
      loadGalleryPhotos();
    }
    if (skipped.length > 0) {
      const { title, description } = skippedPhotosToast(skipped);
      toast.error(title, { description });
    }
  };

  const handleSetFeatured = async (photoId: string) => {
    // Unset all, then set the selected one
    await supabase
      .from('instructor_photos')
      .update({ is_featured: false })
      .eq('leader_id', leader.id);

    await supabase
      .from('instructor_photos')
      .update({ is_featured: true })
      .eq('id', photoId);

    loadGalleryPhotos();
    toast.success('Featured image updated');
  };

  const handleDeleteGalleryPhoto = async (photoId: string) => {
    await supabase.from('instructor_photos').delete().eq('id', photoId);
    loadGalleryPhotos();
    toast.success('Photo removed');
  };

  const showUploadError = (message: string) => {
    const { title, description } = uploadErrorToast(message);
    if (description) toast.error(title, { description });
    else toast.error(title);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file || !user) {
      input.value = '';
      return;
    }

    const problem = validateImageFile(file);
    if (problem) {
      input.value = '';
      showUploadError(problem);
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${user.id}/profile.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('workshop-images')
        .upload(path, withImageType(file), { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('workshop-images')
        .getPublicUrl(path);

      const { error: updateError } = await supabase
        .from('workshop_leaders')
        .update({ profile_photo_url: urlData.publicUrl })
        .eq('id', leader.id);

      if (updateError) throw updateError;

      toast.success('Profile photo updated');
      onUpdate();
    } catch (err) {
      showUploadError(storageUploadMessage(err));
    } finally {
      setUploading(false);
      input.value = '';
    }
  };

  return (
    <>
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Your Profile</CardTitle>
          {!editing && (
            <Button size="sm" variant="outline" onClick={() => handleSetEditing(true)} className="gap-2">
              <Save className="w-3 h-3" />
              Edit Profile
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          This is how photographers will see you on your workshop listings.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-6">
          {/* Profile Photo */}
          <div className="flex-shrink-0 text-center">
            <div className="relative w-28 h-28 rounded-full overflow-hidden bg-muted border-2 border-border mx-auto">
              {leader.profile_photo_url ? (
                <img
                  src={leader.profile_photo_url}
                  alt={leader.business_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-muted/50">
                  <User className="w-10 h-10 text-muted-foreground/50" />
                </div>
              )}
            </div>
            <label className="mt-3 block">
              <input
                type="file"
                accept={IMAGE_ACCEPT}
                className="hidden"
                onChange={handlePhotoUpload}
                disabled={uploading}
              />
              <Button variant="outline" size="sm" className="gap-1.5" asChild disabled={uploading}>
                <span>
                  <Upload className="w-3 h-3" />
                  {uploading ? 'Uploading...' : leader.profile_photo_url ? 'Change Photo' : 'Add Photo'}
                </span>
              </Button>
            </label>
          </div>

          {/* Profile Fields */}
          {editing ? (
            <div className="flex-1 space-y-4">
              <div>
                <Label htmlFor="business_name">Business Name</Label>
                <Input
                  id="business_name"
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  placeholder="Your photography business or name"
                />
              </div>
              <div>
                <Label htmlFor="bio">Short Bio</Label>
                <Textarea
                  id="bio"
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  placeholder="A 1-2 sentence summary — this appears on cards and search results."
                  rows={2}
                />
                <p className="text-xs text-muted-foreground mt-1">Short excerpt shown on workshop cards and instructor listings.</p>
              </div>
              <div>
                <Label>Full Biography</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  This is your public profile page. Help photographers understand who you are. Consider including:
                </p>
                <ul className="text-xs text-muted-foreground mb-3 space-y-0.5 ml-4 list-disc">
                  <li>Your photography background and how you got started</li>
                  <li>What you specialize in (astrophotography, landscapes, deep sky, etc.)</li>
                  <li>What students can expect from your workshops</li>
                  <li>Notable achievements, publications, or exhibitions</li>
                </ul>
                <WorkshopEditor
                  content={form.full_bio}
                  onChange={(html) => setForm({ ...form, full_bio: html })}
                  placeholder="Tell your story — who are you, what drives your photography, and why should someone join your workshop?"
                />
              </div>
              <div>
                <Label htmlFor="website_url">Website</Label>
                <Input
                  id="website_url"
                  value={form.website_url}
                  onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                  placeholder="https://yourwebsite.com"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} disabled={saving} className="gap-2">
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Profile'}
                </Button>
                <Button variant="outline" onClick={() => {
                  setForm({
                    business_name: leader.business_name,
                    bio: leader.bio || '',
                    full_bio: leader.full_bio || '',
                    website_url: leader.website_url || '',
                  });
                  handleSetEditing(false);
                }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground">{leader.business_name}</h3>
              {leader.bio ? (
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{leader.bio}</p>
              ) : (
                <p className="text-sm text-muted-foreground/60 mt-2 italic">No bio yet — click Edit Profile to add one.</p>
              )}
              {leader.website_url ? (
                <a href={leader.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-gold hover:underline mt-3">
                  <span>{leader.website_url.replace(/^https?:\/\//, '')}</span>
                </a>
              ) : (
                <p className="text-sm text-muted-foreground/60 mt-2 italic">No website added yet.</p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Profile Gallery Photos */}
    <Card className="mt-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Profile Gallery</CardTitle>
        <p className="text-sm text-muted-foreground">
          Showcase your work on your instructor profile. The featured image appears large on the right side of your profile page.
        </p>
      </CardHeader>
      <CardContent>
        {/* Upload button */}
        <label className="mb-4 block">
          <input
            type="file"
            accept={IMAGE_ACCEPT}
            multiple
            className="hidden"
            onChange={handleGalleryUpload}
            disabled={uploadingGallery}
          />
          <Button variant="outline" size="sm" className="gap-1.5" asChild disabled={uploadingGallery}>
            <span>
              <ImagePlus className="w-4 h-4" />
              {uploadingGallery ? 'Uploading...' : 'Add Photos'}
            </span>
          </Button>
        </label>

        {/* Photo grid */}
        {galleryPhotos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {galleryPhotos.map((photo) => (
              <div key={photo.id} className="relative group rounded-lg overflow-hidden border border-border">
                <img
                  src={photo.photo_url}
                  alt=""
                  className="w-full aspect-[3/2] object-cover"
                />
                {photo.is_featured && (
                  <div className="absolute top-1.5 left-1.5">
                    <div className="bg-[#c9922a] text-white px-1.5 py-0.5 rounded text-[10px] font-semibold flex items-center gap-1">
                      <Star className="w-2.5 h-2.5 fill-current" />
                      Featured
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {!photo.is_featured && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-white hover:text-[#c9922a] hover:bg-white/20"
                      onClick={() => handleSetFeatured(photo.id)}
                      title="Set as featured"
                    >
                      <Star className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-white hover:text-red-400 hover:bg-white/20"
                    onClick={() => handleDeleteGalleryPhoto(photo.id)}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 border border-dashed border-border rounded-lg">
            <ImagePlus className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No gallery photos yet. Add images that showcase your photography and workshops.</p>
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
};
